import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLoading } from '../context/LoadingContext';
import {
  BeakerIcon,
  ChartBarIcon,
  CpuChipIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CommandLineIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    title: "Advanced AI Technology",
    description: "Leverage state-of-the-art machine learning models and neural networks for intelligent solutions that adapt to your needs.",
    icon: CpuChipIcon,
    color: "blue",
  },
  {
    title: "Real-time Analytics",
    description: "Get instant insights with our powerful analytics engine, helping you make data-driven decisions faster than ever.",
    icon: ChartBarIcon,
    color: "purple",
  },
  {
    title: "Innovation Labs",
    description: "Stay ahead with our continuous research and development, bringing you the latest in AI technology.",
    icon: BeakerIcon,
    color: "pink",
  },
];

const benefits = [
  {
    title: "Smart Automation",
    description: "Automate complex workflows with intelligent systems that learn and adapt to your processes.",
    icon: SparklesIcon,
    color: "yellow",
  },
  {
    title: "Enterprise Security",
    description: "Rest easy with our enterprise-grade security measures and data protection protocols.",
    icon: ShieldCheckIcon,
    color: "green",
  },
  {
    title: "AI-Powered Insights",
    description: "Transform raw data into actionable insights with our advanced AI analytics engine.",
    icon: LightBulbIcon,
    color: "orange",
  },
];

const capabilities = [
  {
    title: "Quick Deployment",
    description: "Deploy AI solutions rapidly with our streamlined implementation process.",
    icon: RocketLaunchIcon,
  },
  {
    title: "Developer Tools",
    description: "Access powerful APIs and SDKs designed for seamless integration.",
    icon: CommandLineIcon,
  },
  {
    title: "Cloud Integration",
    description: "Easily integrate with your existing cloud infrastructure and services.",
    icon: CloudArrowUpIcon,
  },
];

export default function LearnMore() {
  const { setLoading } = useLoading();

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [setLoading]);

  const getGradientClass = (color: string) => {
    const gradients = {
      blue: 'from-blue-500/20 to-blue-500/0',
      purple: 'from-purple-500/20 to-purple-500/0',
      pink: 'from-pink-500/20 to-pink-500/0',
      yellow: 'from-yellow-500/20 to-yellow-500/0',
      green: 'from-green-500/20 to-green-500/0',
      orange: 'from-orange-500/20 to-orange-500/0',
    };
    return gradients[color as keyof typeof gradients] || gradients.blue;
  };

  const getIconClass = (color: string) => {
    const colors = {
      blue: 'text-blue-400',
      purple: 'text-purple-400',
      pink: 'text-pink-400',
      yellow: 'text-yellow-400',
      green: 'text-green-400',
      orange: 'text-orange-400',
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900/95 to-gray-900" />
        </div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24"
        >
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl sm:text-6xl font-bold text-white mb-8 leading-tight"
            >
              Discover the Power of{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
                NEXON AI
              </span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto"
            >
              Experience the next generation of AI technology. Our advanced platform combines
              cutting-edge machine learning with intuitive design to transform the way you work.
            </motion.p>
          </div>
        </motion.div>
      </div>

      {/* Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Advanced Features
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Our platform is built with cutting-edge technology to provide you with the most powerful AI capabilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
                className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-800/50 p-8 backdrop-blur-sm transition-all duration-300 hover:border-gray-700 hover:bg-gray-800/70"
              >
                <div className={`absolute top-0 right-0 h-64 w-64 rounded-full blur-3xl -z-10 bg-gradient-to-br ${getGradientClass(feature.color)} opacity-25 group-hover:opacity-50 transition-opacity duration-300`} />
                <div className="relative flex flex-col h-full">
                  <div className={`p-3.5 rounded-xl ${getIconClass(feature.color)} bg-gray-900/50 ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-300 group-hover:scale-110 w-fit`}>
                    <feature.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mt-6 mb-4">{feature.title}</h3>
                  <p className="text-gray-300 text-base flex-grow">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Transform your workflow with our powerful AI solutions designed for the modern enterprise
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
                className="group relative overflow-hidden rounded-xl border border-gray-700 bg-gray-800/50 p-8 backdrop-blur-sm transition-all duration-300 hover:border-gray-600 hover:bg-gray-700/70"
              >
                <div className={`absolute top-0 right-0 h-64 w-64 rounded-full blur-3xl -z-10 bg-gradient-to-br ${getGradientClass(benefit.color)} opacity-25 group-hover:opacity-50 transition-opacity duration-300`} />
                <div className="relative flex flex-col h-full">
                  <div className={`p-3.5 rounded-xl ${getIconClass(benefit.color)} bg-gray-900/50 ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-300 group-hover:scale-110 w-fit`}>
                    <benefit.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mt-6 mb-4">{benefit.title}</h3>
                  <p className="text-gray-300 text-base flex-grow">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 bg-gray-800/30 backdrop-blur-sm p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-800/10 to-gray-800/30 -z-10" />
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-4">
                Platform Capabilities
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Built for developers, designed for scale
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={capability.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
                  className="relative group text-center"
                >
                  <div className="p-5 rounded-xl text-blue-400 bg-gray-900/50 mb-6 mx-auto inline-block ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-300 group-hover:scale-110">
                    <capability.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{capability.title}</h3>
                  <p className="text-gray-300 text-base">{capability.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
